/**
 * Edge labels — single source of truth for relationship → display string.
 *
 * Mirrors design/kaiva-canvas/project/components/script.jsx#EDGE_LABEL.
 * NEXT_STEP and RELATES_TO render with no label (the arrow alone says "next" /
 * the relationship is implicit). Legacy backend types are normalised here too
 * so the UI never has to special-case them.
 */

export const EDGE_LABEL: Record<string, string> = {
  NEXT_STEP: '',
  TRIGGERS: 'starts',
  USES: 'uses',
  RESPONSIBLE_FOR: 'owns',
  GOVERNED_BY: 'rule',
  MEASURED_BY: 'measured by',
  MITIGATES: 'mitigates',
  RELATES_TO: '',
  // Legacy / synonym aliases — keep until backend cleanup
  REFERENCES: 'uses',
  APPROVES: 'rule',
  FILLS_ROLE: 'owns',
  PROCESSES: '',
  WORKS_AROUND: 'mitigates',
};

/**
 * Resolve a relationship type to its display label. Returns the empty string
 * for relationships that should render without a pill (NEXT_STEP, RELATES_TO).
 * For unknown types we fall back to a lowercased version of the type name —
 * better than rendering a screaming MACHINE_CASE.
 */
export function getEdgeLabel(type: string | null | undefined): string {
  if (!type) return '';
  if (type in EDGE_LABEL) return EDGE_LABEL[type];
  return type.toLowerCase().replace(/_/g, ' ');
}

/**
 * Inverse of getEdgeLabel — given a rendered label string, recover the
 * canonical relationship type. Used as a fallback when a persisted edge
 * is missing `data.canonicalType` (legacy rows, or rows written before
 * D.1 fixed the round-trip bug).
 */
export function inferCanonicalType(label: string | null | undefined): string {
  if (!label) return 'NEXT_STEP';
  switch (label.trim().toLowerCase()) {
    case '':
    case 'next':
      return 'NEXT_STEP';
    case 'starts':
    case 'triggers':
      return 'TRIGGERS';
    case 'uses':
      return 'USES';
    case 'owns':
    case 'responsible':
      return 'RESPONSIBLE_FOR';
    case 'rule':
    case 'governed by':
      return 'GOVERNED_BY';
    case 'measured by':
      return 'MEASURED_BY';
    case 'mitigates':
      return 'MITIGATES';
    default:
      return 'RELATES_TO';
  }
}
